import getClientApiRequestTransformer from '@/api/transformers/auth.request.transformer';
import getSocketIdRequestTransformer from '@/api/transformers/socketId.request.transformer';
import Product from '@/models/product';
import Ingredient from '@/models/ingredient';
import getIngredientsTransformer from '@/api/transformers/menu/ingredientsTransformer';
import getCategoriesTransformer from '@/api/transformers/menu/categoriesTransformer';
import getProductsTransformer from '@/api/transformers/menu/productsTransformer';

export default {
  async fetchCategories(): Promise<any> {
    return window.axios.get('/menu/category/list', {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getCategoriesTransformer()
    });
  },
  async fetchCategoriesForPos(): Promise<any> {
    return window.axios.get('/pos/category/list', {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getCategoriesTransformer()
    });
  },
  async fetchIngredients(): Promise<any> {
    return window.axios.get('/menu/ingredient/list', {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getIngredientsTransformer()
    });
  },
  async createCategory(name: string): Promise<any> {
    return window.axios.post('/menu/category/create', { name }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async renameCategory({ id, name }: any): Promise<any> {
    return window.axios.patch(`/menu/category/edit/${id}`, { name }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async removeCategoryWithoutProducts(categoryId: string): Promise<any> {
    return window.axios.delete(`/menu/category/remove/${categoryId}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async removeCategoryWithProducts(categoryId: string): Promise<any> {
    return window.axios.delete(`/menu/category/purge/${categoryId}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async sortCategories(payload: any): Promise<any> {
    return window.axios.patch('/menu/category/resort', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async toggleCategoryVisibilityInPos(categoryId: any): Promise<any> {
    return window.axios.patch(`/menu/category/display_in_pos/${categoryId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async toggleCategoryVisibilityInWidget(categoryId: any): Promise<any> {
    return window.axios.patch(`/menu/category/display_in_widget/${categoryId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async sortProducts(payload: any): Promise<any> {
    return window.axios.patch('/menu/product/resort', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async createProduct(product: Product): Promise<any> {
    return window.axios.post('/menu/product/create', product, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async importProduct({ productId, categoryId }: any): Promise<any> {
    return window.axios.post(`/menu/product/import/${productId}`, { categoryId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async editProduct({ id, editedFields }: any): Promise<any> {
    return window.axios.patch(`/menu/product/edit/${id}`, editedFields, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async removeProduct(productId: string): Promise<any> {
    return window.axios.delete(`/menu/product/remove/${productId}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async toggleProductFavorite(productId: string): Promise<any> {
    return window.axios.patch(`/menu/product/favorite/toggle/${productId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async showProductInWidget(productId: any): Promise<any> {
    return window.axios.patch(`/menu/product/show/${productId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async hideProductInWidget(productId: any): Promise<any> {
    return window.axios.patch(`/menu/product/hide/${productId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async showProductInPos(productId: any): Promise<any> {
    return window.axios.patch(`/menu/product/display_in_pos/show/${productId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async hideProductInPos(productId: any): Promise<any> {
    return window.axios.patch(`/menu/product/display_in_pos/hide/${productId}`, null, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async createIngredient(ingredient: Ingredient): Promise<any> {
    return window.axios.post('/menu/ingredient/create', ingredient, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async editIngredient({ id, editedFields }: any): Promise<any> {
    return window.axios.patch(`/menu/ingredient/edit/${id}`, editedFields, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async removeIngredient(ingredientId: string): Promise<any> {
    return window.axios.delete(`/menu/ingredient/remove/${ingredientId}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async searchIngredientsByIngredientName(searchText: string): Promise<any> {
    return window.axios.get(`/menu/ingredient/search/title?query=${searchText}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getIngredientsTransformer()
    });
  },
  async searchImportProductsByProductName(searchText: string): Promise<any> {
    return window.axios.get(`/menu/product/import/search/title?query=${searchText}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getProductsTransformer()
    });
  },
  async getProductsByTitleAndPlaceIds(title: string, placeIds: string[], addedProductIds: string[]): Promise<any> {
    return window.axios.get(
      `/menu/product/search/title-and-places?query=${title}&placeIds=${placeIds.join(',')}&addedProductIds=${addedProductIds.join(',')}`,
      {
        transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
        transformResponse: getProductsTransformer(),
      }
    );
  },
  async uploadImage(file: Blob): Promise<any> {
    const formData = new FormData();

    formData.append('menuitem_image[file]', file);

    return window.axios({
      method: 'POST',
      url: '/menu/image/upload',
      data: formData,
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  async checkImageIsExist(payload: { file: Blob, hash: string }): Promise<any> {
    return window.axios.post('/menu/image/check', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  }
};
